<template>
  <div class="order-panel">
    <div v-if="!supported" class="caption text-center pa-6" style="color: grey">
      <v-icon size="40" color="grey lighten-1">mdi-information-outline</v-icon>
      <div class="mt-2">{{ use_language.supports_only_facebook }}</div>
    </div>

    <div v-else-if="!chatId" class="caption text-center pa-6" style="color: grey">
      {{ use_language.select_chat_create_order }}
    </div>

    <div v-else class="pa-3">
      <!-- คลัง -->
      <v-card outlined class="mb-3">
        <v-card-title class="body-2 py-2">
          <v-icon size="18" color="primary" class="mr-1">mdi-warehouse</v-icon>
          {{ use_language.warehouse }}
        </v-card-title>
        <v-divider />
        <v-card-text class="py-2">
          <v-select
            v-model="warehouseId"
            :items="warehouseList"
            item-text="name"
            item-value="id"
            outlined dense hide-details
            :loading="loadingWarehouse"
          />
        </v-card-text>
      </v-card>

      <!-- ค้นหา / รายการสินค้า -->
      <v-card outlined class="mb-3">
        <v-card-title class="body-2 py-2">
          <v-icon size="18" color="primary" class="mr-1">mdi-cart-plus</v-icon>
          {{ use_language.product }}
        </v-card-title>
        <v-divider />
        <v-card-text class="py-2">
          <v-autocomplete
            v-model="selectedStock"
            :items="stockOptions"
            :search-input.sync="stockKeyword"
            :loading="searchingStock"
            item-text="stockFullname"
            return-object
            outlined dense hide-details
            :no-data-text="use_language.product_not_found"
            :placeholder="use_language.type_product_sku_barcode"
            prepend-inner-icon="mdi-magnify"
            @change="onAddStock"
          />

          <v-simple-table v-if="productList.length > 0" dense class="mt-2">
            <thead>
              <tr>
                <th>{{ use_language.product }}</th>
                <th style="width: 80px" class="text-center">{{ use_language.quantity }}</th>
                <th style="width: 100px" class="text-right">{{ use_language.price }}</th>
                <th style="width: 32px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, idx) in productList" :key="idx">
                <td>
                  <div style="font-size: 13px; font-weight: 600">{{ p.stockFullname }}</div>
                  <div class="caption" style="color: grey">SKU: {{ p.sku }}</div>
                </td>
                <td>
                  <v-text-field
                    v-model.number="p.quantity"
                    type="number"
                    outlined dense hide-details
                    min="1"
                    @change="recalc"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    v-model.number="p.salePrice"
                    type="number"
                    outlined dense hide-details
                    @change="recalc"
                  />
                </td>
                <td>
                  <v-btn icon small @click="removeStock(idx)">
                    <v-icon size="16" color="error">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div v-else class="caption text-center py-3" style="color: grey">
            {{ use_language.no_products_yet }}
          </div>
        </v-card-text>
      </v-card>

      <!-- COD -->
      <v-card outlined class="mb-3">
        <v-card-title class="body-2 py-2">
          <v-icon size="18" color="primary" class="mr-1">mdi-cash-multiple</v-icon>
          {{ use_language.payment_type }}
        </v-card-title>
        <v-divider />
        <v-card-text class="py-2">
          <v-radio-group v-model="isCOD" row hide-details class="mt-0">
            <v-radio label="COD" :value="true" />
            <v-radio :label="use_language.paid" :value="false" />
          </v-radio-group>
        </v-card-text>
      </v-card>

      <!-- ที่อยู่ -->
      <v-card outlined class="mb-3">
        <v-card-title class="body-2 py-2">
          <v-icon size="18" color="primary" class="mr-1">mdi-map-marker-outline</v-icon>
          {{ use_language.receiver_address }}
          <v-spacer />
          <v-btn icon small :loading="loadingAddress" @click="autofillFromCustomer" :title="'auto-fill ' + use_language.from_customer_source">
            <v-icon size="16">mdi-account-arrow-left</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="py-2">
          <v-text-field v-model="address.name" outlined dense hide-details :label="use_language.name" class="mb-2" />
          <v-text-field v-model="address.phone" outlined dense hide-details :label="use_language.tel" class="mb-2" />
          <v-textarea v-model="address.address" outlined dense hide-details rows="2" :label="use_language.address" />
        </v-card-text>
      </v-card>

      <!-- ขนส่ง -->
      <v-card outlined class="mb-3">
        <v-card-title class="body-2 py-2">
          <v-icon size="18" color="primary" class="mr-1">mdi-truck-delivery-outline</v-icon>
          {{ use_language.transport }}
        </v-card-title>
        <v-divider />
        <v-card-text class="py-2">
          <v-select
            v-model="expressUniqueID"
            :items="expressList"
            item-text="expressCompanyName"
            item-value="uniqueID"
            outlined dense hide-details
          />
        </v-card-text>
      </v-card>

      <!-- Slip (เฉพาะ ชำระแล้ว) -->
      <v-card v-if="isCOD === false" outlined class="mb-3">
        <v-card-title class="body-2 py-2">
          <v-icon size="18" color="primary" class="mr-1">mdi-receipt-text</v-icon>
          {{ use_language.attach }} Slip
        </v-card-title>
        <v-divider />
        <v-card-text class="py-2">
          <v-file-input
            v-model="slipFile"
            accept="image/*"
            outlined dense hide-details
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            :label="use_language.choose_file + ' slip'"
          />
        </v-card-text>
      </v-card>

      <!-- ราคารวม + submit -->
      <v-card outlined class="mb-3">
        <v-card-text>
          <div class="d-flex">
            <span>{{ use_language.total_products }}</span>
            <v-spacer />
            <span>฿{{ formatPrice(productTotal) }}</span>
          </div>
          <div class="d-flex mt-2">
            <span>{{ use_language.delivery_fee }}</span>
            <v-spacer />
            <v-text-field
              v-model.number="deliveryCost"
              type="number"
              outlined dense hide-details
              style="max-width: 100px"
            />
          </div>
          <v-divider class="my-2" />
          <div class="d-flex font-weight-bold">
            <span>{{ use_language.total_all }}</span>
            <v-spacer />
            <span>฿{{ formatPrice(grandTotal) }}</span>
          </div>
        </v-card-text>
      </v-card>

      <v-btn
        block color="primary"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="submitOrder"
      >
        <v-icon left>mdi-check</v-icon>{{ use_language.create_order }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import {
  orderService_dotnet,
  shopService_dotnet,
  stockService_dotnet,
  generalService_dotnet,
} from "@/website/global";
import { PLATFORM } from "../constants";
import { get_languages } from '@/website/global_function'

export default {
  name: "OrderPanel",
  props: {
    shopId: { type: [String, Number], required: true },
    staffId: { type: [String, Number], required: true },
    chatId: { type: [String, Number], default: null },
    platformId: { type: [String, Number], default: null },
    shopCustomerId: { type: [String, Number], default: null },
    chatTitle: { type: Object, default: null },
  },
  data: () => ({
    use_language: {},
    // master
    warehouseList: [],
    warehouseId: null,
    loadingWarehouse: false,
    expressList: [],
    expressUniqueID: null,
    // stock search
    stockKeyword: "",
    stockOptions: [],
    selectedStock: null,
    searchingStock: false,
    searchDebounce: null,
    // order
    productList: [],
    isCOD: null,
    address: { name: "", phone: "", address: "", postcodeMappingId: 0, shopCustomerID: 0 },
    deliveryCost: 0,
    slipFile: null,
    loadingAddress: false,
    submitting: false,
    masterLoaded: false,
  }),
  computed: {
    supported() {
      const p = parseInt(this.platformId);
      return p === PLATFORM.FACEBOOK || p === PLATFORM.LINE;
    },
    productTotal() {
      return this.productList.reduce(
        (s, p) => s + (Number(p.salePrice || 0) * Number(p.quantity || 0)),
        0
      );
    },
    grandTotal() {
      return this.productTotal + Number(this.deliveryCost || 0);
    },
    canSubmit() {
      return (
        this.productList.length > 0 &&
        this.isCOD !== null &&
        this.address.name &&
        this.address.phone &&
        this.address.address &&
        this.expressUniqueID
      );
    },
  },
  watch: {
    stockKeyword(val) {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.searchStock(val), 350);
    },
    chatId() {
      // chat ใหม่ → reset (เฉพาะข้อมูลที่ผูกกับ chat)
      this.productList = [];
      this.isCOD = null;
      this.address = { name: "", phone: "", address: "", postcodeMappingId: 0, shopCustomerID: 0 };
      this.deliveryCost = 0;
      this.slipFile = null;
      if (this.supported && this.shopCustomerId) this.autofillFromCustomer();
    },
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
  },
  methods: {
    get_languages,
    formatPrice(v) {
      return Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    recalc() { /* trigger Vue reactivity by no-op */ },

    /** เรียกจาก parent ตอนที่ tab "สร้างออเดอร์" ถูกเปิดเป็นครั้งแรก */
    ensureMasterLoaded() {
      if (this.masterLoaded || !this.supported) return;
      this.loadMaster();
    },

    async loadMaster() {
      if (this.masterLoaded) return;
      this.masterLoaded = true;
      this.loadingWarehouse = true;
      const headers = { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("authorization_token") };
      try {
        const [wh, ex] = await Promise.all([
          axios.post(shopService_dotnet + "Shop/get-warehouse-list", { shopID: this.shopId }, { headers }),
          axios.post(shopService_dotnet + "Shop/get-shop-express-addorder", { shopID: this.shopId }, { headers }),
        ]);
        this.warehouseList = wh.data || [];
        if (this.warehouseList.length > 0) this.warehouseId = this.warehouseList[0].id;
        this.expressList = ex.data || [];
      } catch (err) {
        this.$swal && this.$swal({ icon: "error", title: this.use_language.load_warehouse_shipping_failed, text: err.message });
      } finally {
        this.loadingWarehouse = false;
      }
    },

    async searchStock(keyword) {
      if (!keyword || keyword.length < 2 || !this.warehouseId) {
        this.stockOptions = [];
        return;
      }
      this.searchingStock = true;
      try {
        const res = await axios.post(
          stockService_dotnet + "Stock/get-stock-list",
          {
            shopID: this.shopId,
            keyword,
            warehouseID: this.warehouseId,
            isActive: true,
            skipStockShopIDList: [],
            skip: 0, take: 30,
            isAscending: true,
            minQuantityAvailable: null,
          },
          { headers: { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("authorization_token") } }
        );
        this.stockOptions = (res.data && res.data.stockList) || [];
      } catch (err) {
        console.error("[search-stock]", err);
        this.stockOptions = [];
      } finally {
        this.searchingStock = false;
      }
    },

    onAddStock(item) {
      if (!item || !item.id) return;
      // ป้องกัน duplicate
      if (this.productList.some((p) => p.stockShopID === item.stockShopID)) {
        this.selectedStock = null;
        return;
      }
      this.productList.push({
        stockShopID: item.stockShopID,
        stockFullname: item.stockFullname,
        sku: item.sku,
        salePrice: item.salePrice || 0,
        quantity: 1,
        productMasterID: item.productMasterID,
        productMasterItemID: item.productMasterItemID,
      });
      this.selectedStock = null;
      this.stockKeyword = "";
    },
    removeStock(idx) { this.productList.splice(idx, 1); },

    async autofillFromCustomer() {
      if (!this.shopCustomerId) return;
      this.loadingAddress = true;
      try {
        const headers = { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("authorization_token") };
        const res = await axios.post(
          shopService_dotnet + "Customer/get-customer-info",
          { shopCustomerID: this.shopCustomerId },
          { headers }
        );
        const list = (res.data && res.data.shippingAddressList) || [];
        if (list.length === 0) {
          this.$swal && this.$swal({ icon: "info", title: this.use_language.customer_no_address });
          return;
        }
        const def = list.find((x) => x.isDefault) || list[0];
        this.address = {
          name: def.name || "",
          phone: def.phone || "",
          address: def.address || "",
          postcodeMappingId: def.postcodeMappingID || 0,
          shopCustomerID: def.shopCustomerID || this.shopCustomerId,
        };
      } catch (err) {
        this.$swal && this.$swal({ icon: "error", title: this.use_language.fetch_customer_failed, text: err.message });
      } finally {
        this.loadingAddress = false;
      }
    },

    async _uploadSlip() {
      if (!this.slipFile) return "";
      const fd = new FormData();
      fd.append("file", this.slipFile);
      fd.append("bucket", "shop-slip");
      try {
        const res = await axios.post(
          generalService_dotnet + "General/upload-file-with-bucket",
          fd,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + localStorage.getItem("authorization_token"),
            },
          }
        );
        return (res.data && res.data.url) || "";
      } catch (e) {
        console.error("[upload-slip]", e);
        return "";
      }
    },

    async submitOrder() {
      if (!this.canSubmit) return;
      this.submitting = true;
      try {
        // แตก ExpressUniqueID = branchExpressID_shopExpressID_expressCompanyId
        let branchExpressID = null, shopExpressID = null, expressCompanyId = null;
        if (this.expressUniqueID) {
          const arr = String(this.expressUniqueID).split("_");
          branchExpressID = parseInt(arr[0]) || null;
          shopExpressID = parseInt(arr[1]) || null;
          expressCompanyId = parseInt(arr[2]) || null;
          if (branchExpressID === 0) branchExpressID = null;
          if (shopExpressID === 0) shopExpressID = null;
        }

        const orderMaster = {
          shopId: this.shopId,
          createdByStaffId: this.staffId,
          salemanId: parseInt(this.staffId),
          shippingStatusId: 0,
          totalPrice: this.productTotal,
          deliveryCost: Number(this.deliveryCost || 0),
          isCOD: this.isCOD,
          codamount: this.isCOD ? this.grandTotal : null,
          warehouseId: this.warehouseId,
          expressCompanyId, shopExpressID, branchExpressID,
          ExpressUniqueID: this.expressUniqueID,
          chatTitleID: this.chatTitle ? this.chatTitle.chatID : null,
          referenceSourceId: 0,
        };
        const orderItem = this.productList.map((p) => ({
          stockShopId: p.stockShopID,
          quantity: p.quantity,
          salePrice: p.salePrice,
          productMasterID: p.productMasterID,
          productMasterItemID: p.productMasterItemID,
        }));

        // upload slip (ถ้ามี)
        let orderPayment = null;
        if (this.isCOD === false && this.slipFile) {
          const url = await this._uploadSlip();
          if (url) {
            orderPayment = [{
              amount: this.grandTotal,
              photoLink: url,
              informDatetime: new Date().toISOString(),
            }];
          }
        }

        const headers = { "Content-Type": "application/json", Authorization: "Bearer " + localStorage.getItem("authorization_token") };
        const res = await axios.post(
          orderService_dotnet + "Order/new-order",
          {
            orderMaster,
            orderItem,
            shippingAddress: this.address,
            shopAddress: { id: 0 },
            orderPayment,
          },
          { headers }
        );
        if (res.data && res.data.status === "success") {
          this.$swal && this.$swal({ icon: "success", title: this.use_language.order_created_success, timer: 1500, showConfirmButton: false });
          this.$emit("created", res.data.orderID);
          // reset
          this.productList = [];
          this.slipFile = null;
        } else {
          throw new Error((res.data && res.data.message) || this.use_language.fail);
        }
      } catch (err) {
        this.$swal && this.$swal({ icon: "error", title: this.use_language.create_order_failed, text: err.message || this.use_language.error });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.order-panel {
  height: 100%;
  overflow-y: auto;
  background: #fafafa;
}
</style>
